import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, rupiah, fmtDate, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Printer } from "lucide-react";
import DocumentIdentityFields from "@/components/DocumentIdentityFields";
import { documentIdentityFormValues } from "@/lib/documentIdentity";

const EMPTY = { customer_name: "", customer_phone: "", customer_address: "", items: [{ description: "", qty: 1, price: 0 }], discount: 0, tax_percent: 0, due_date: "", notes: "", status: "belum_lunas", business_name: "", business_logo: "", business_whatsapp: "" };

export default function Invoices() {
  const [invoices, setInvoices] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [products, setProducts] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [settings, setSettings] = useState(null);

  const load = async () => {
    const [invoiceResult, productResult, settingsResult] = await Promise.allSettled([
      API.get("/invoices"),
      API.get("/products", { params: { all: true } }),
      API.get("/settings"),
    ]);
    setInvoices(invoiceResult.status === "fulfilled" ? invoiceResult.value.data : []);
    if (productResult.status === "fulfilled") setProducts(productResult.value.data);
    if (settingsResult.status === "fulfilled") setSettings(settingsResult.value.data);
  };
  useEffect(() => { load(); }, []);

  const PRODUCT_OPTIONS = products.flatMap((p) =>
    (p.variants || []).map((v) => ({ label: `${p.name} • ${v.label}`, desc: `${p.name} (${v.label})`, price: Number(v.price) || 0 }))
  );

  const save = async () => {
    try {
      const payload = { ...form, discount: Number(form.discount) || 0, tax_percent: Number(form.tax_percent) || 0, items: form.items.filter((i) => i.description).map((i) => ({ ...i, qty: Number(i.qty) || 0, price: Number(i.price) || 0 })) };
      if (editId) {
        await API.put(`/invoices/${editId}`, payload);
        toast.success("Invoice diperbarui");
      } else {
        await API.post("/invoices", payload);
        toast.success("Invoice dibuat");
      }
      setForm(null);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const openEdit = (inv) => {
    setForm({
      customer_name: inv.customer_name, customer_phone: inv.customer_phone || "", customer_address: inv.customer_address || "",
      items: inv.items, discount: inv.discount || 0, tax_percent: inv.tax_percent || 0,
      due_date: inv.due_date || "", notes: inv.notes || "", status: inv.status || "belum_lunas",
      ...documentIdentityFormValues(inv, settings || {}),
    });
    setEditId(inv.id);
  };

  const setStatus = async (inv, status) => {
    try {
      await API.put(`/invoices/${inv.id}`, {
        customer_name: inv.customer_name, customer_phone: inv.customer_phone || "", customer_address: inv.customer_address || "",
        items: inv.items, discount: inv.discount || 0, tax_percent: inv.tax_percent, due_date: inv.due_date || "", notes: inv.notes || "", status,
        business_name: inv.business_name || "", business_logo: inv.business_logo || "", business_whatsapp: inv.business_whatsapp || "",
      });
      toast.success("Status diperbarui");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async () => {
    if (!confirmDelete || deleting) return;
    setDeleting(true);
    try {
      await API.delete(`/invoices/${confirmDelete.id}`);
      setInvoices((rows) => (rows || []).filter((row) => row.id !== confirmDelete.id));
      setConfirmDelete(null);
      toast.success("Invoice berhasil dihapus");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setDeleting(false);
    }
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
  const labelCls = "text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5";
  const subtotal = form ? form.items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.price) || 0), 0) : 0;
  const estTax = (subtotal * (Number(form?.tax_percent) || 0)) / 100;
  const discount = Math.min(Math.max(Number(form?.discount) || 0, 0), subtotal + estTax);
  const estTotal = subtotal + estTax - discount;

  return (
    <div data-testid="admin-invoices">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Invoice / Faktur</h1>
          <p className="text-sm text-slate-500">Buat nota &amp; tagihan otomatis untuk klien</p>
        </div>
        <button onClick={() => { setForm({ ...EMPTY, ...documentIdentityFormValues({}, settings || {}), items: [{ description: "", qty: 1, price: 0 }] }); setEditId(null); }} data-testid="add-invoice" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-sm font-bold rounded-md transition-colors">
          <Plus className="h-4 w-4" /> Buat Invoice
        </button>
      </div>

      <div className="hidden sm:block bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="min-w-[760px] w-full text-sm" data-testid="invoices-table">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3">No. Invoice</th>
              <th className="px-5 py-3">Pelanggan</th>
              <th className="px-5 py-3">Total</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Tanggal</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(invoices || []).map((inv) => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="hover:bg-slate-50">
                <td className="px-5 py-3 font-mono-accent text-xs font-bold">{inv.invoice_number}</td>
                <td className="px-5 py-3">{inv.customer_name}</td>
                <td className="px-5 py-3 font-bold">{rupiah(inv.total)}</td>
                <td className="px-5 py-3">
                  <select value={inv.status} onChange={(e) => setStatus(inv, e.target.value)} data-testid={`invoice-status-${inv.id}`} className="text-xs font-bold border border-slate-300 rounded px-2 py-1.5 bg-white">
                    <option value="belum_lunas">Belum Lunas</option>
                    <option value="lunas">Lunas</option>
                  </select>
                </td>
                <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(inv.created_at)}</td>
                <td className="px-5 py-3 whitespace-nowrap">
                  <button onClick={() => openEdit(inv)} data-testid={`invoice-edit-${inv.id}`} className="mr-2 inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-xs font-bold rounded-md hover:border-[#0B1F3A] transition-colors">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <Link to={`/admin/invoice/${inv.id}`} data-testid={`invoice-print-${inv.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0B1F3A] text-white text-xs font-bold rounded-md hover:bg-[#132D52] transition-colors">
                    <Printer className="h-3.5 w-3.5" /> Cetak
                  </Link>
                  <button onClick={() => setConfirmDelete(inv)} data-testid={`invoice-delete-${inv.id}`} aria-label={`Hapus invoice ${inv.invoice_number}`} title="Hapus invoice" className="ml-2 inline-flex items-center justify-center rounded-md border border-red-200 p-1.5 text-red-600 transition-colors hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {invoices && invoices.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Belum ada invoice.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 sm:hidden" data-testid="invoices-mobile-list">
        {(invoices || []).map((inv) => (
          <article key={inv.id} className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-mono-accent text-xs font-bold text-[#0B1F3A]">{inv.invoice_number}</p>
                <p className="mt-1 truncate font-semibold">{inv.customer_name}</p>
                <p className="text-xs text-slate-500">{fmtDate(inv.created_at)}</p>
              </div>
              <p className="shrink-0 font-bold">{rupiah(inv.total)}</p>
            </div>
            <select value={inv.status} onChange={(e) => setStatus(inv, e.target.value)} data-testid={`invoice-mobile-status-${inv.id}`} className="mt-3 w-full rounded border border-slate-300 bg-white px-2 py-2 text-xs font-bold">
              <option value="belum_lunas">Belum Lunas</option>
              <option value="lunas">Lunas</option>
            </select>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <button onClick={() => openEdit(inv)} className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 px-2 py-2 text-xs font-bold"><Pencil className="h-3.5 w-3.5" /> Edit</button>
              <Link to={`/admin/invoice/${inv.id}`} className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[#0B1F3A] px-2 py-2 text-xs font-bold text-white"><Printer className="h-3.5 w-3.5" /> Cetak</Link>
              <button onClick={() => setConfirmDelete(inv)} className="inline-flex items-center justify-center gap-1.5 rounded-md border border-red-200 px-2 py-2 text-xs font-bold text-red-600"><Trash2 className="h-3.5 w-3.5" /> Hapus</button>
            </div>
          </article>
        ))}
        {invoices && invoices.length === 0 && <div className="rounded-md border border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-400">Belum ada invoice.</div>}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setForm(null)} />
          <div className="relative bg-white rounded-md border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" data-testid="invoice-dialog">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold">{editId ? "Edit Invoice" : "Buat Invoice Baru"}</h2>
              <button onClick={() => setForm(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="mb-4">
              <DocumentIdentityFields value={form} onChange={setForm} settings={settings || {}} testIdPrefix="invoice" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Nama Pelanggan *</label>
                <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="inv-customer" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>No. Telepon</label>
                <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="inv-phone" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Alamat</label>
                <input value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} data-testid="inv-address" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className={labelCls + " mb-0"}>Item Tagihan</label>
                  <button onClick={() => setForm({ ...form, items: [...form.items, { description: "", qty: 1, price: 0 }] })} data-testid="inv-add-item" className="text-xs font-bold text-[#C9A227] hover:underline">
                    + Tambah Item
                  </button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, i) => (
                    <div key={i} className="border border-slate-200 rounded-md p-2.5 space-y-2">
                      <select
                        value=""
                        onChange={(e) => {
                          const opt = PRODUCT_OPTIONS[Number(e.target.value)];
                          if (opt) {
                            const arr = [...form.items];
                            arr[i] = { ...it, description: opt.desc, price: opt.price };
                            setForm({ ...form, items: arr });
                          }
                        }}
                        data-testid={`inv-item-product-${i}`}
                        className={inputCls + " text-slate-500"}
                      >
                        <option value="">— Pilih dari data Produk (opsional) —</option>
                        {PRODUCT_OPTIONS.map((o, j) => (
                          <option key={j} value={j}>{o.label} — {rupiah(o.price)}</option>
                        ))}
                      </select>
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <input value={it.description} onChange={(e) => { const arr = [...form.items]; arr[i] = { ...it, description: e.target.value }; setForm({ ...form, items: arr }); }} data-testid={`inv-item-desc-${i}`} className={inputCls + " col-span-12 sm:col-span-6"} placeholder="Deskripsi barang/jasa" />
                        <input type="number" min="1" value={it.qty} onChange={(e) => { const arr = [...form.items]; arr[i] = { ...it, qty: e.target.value }; setForm({ ...form, items: arr }); }} data-testid={`inv-item-qty-${i}`} className={inputCls + " col-span-4 sm:col-span-2"} placeholder="Qty" />
                        <input type="number" min="0" value={it.price} onChange={(e) => { const arr = [...form.items]; arr[i] = { ...it, price: e.target.value }; setForm({ ...form, items: arr }); }} data-testid={`inv-item-price-${i}`} className={inputCls + " col-span-7 sm:col-span-3"} placeholder="Harga" />
                        <button onClick={() => setForm({ ...form, items: form.items.filter((_, x) => x !== i) })} aria-label="Hapus item invoice" className="col-span-1 flex justify-center text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls}>Diskon (Rp)</label>
                <input type="number" min="0" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} data-testid="inv-discount" className={inputCls} placeholder="0" />
              </div>
              <div>
                <label className={labelCls}>Pajak (%)</label>
                <input type="number" min="0" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} data-testid="inv-tax" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Jatuh Tempo</label>
                <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="inv-due" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Catatan</label>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="inv-notes" className={inputCls} />
              </div>
            </div>
            <div className="mt-4 text-right text-sm space-y-1">
              {estTax > 0 && <p className="text-slate-500">Pajak ({form.tax_percent}%): <span className="font-semibold">+ {rupiah(estTax)}</span></p>}
              {discount > 0 && <p className="text-slate-500">Diskon: <span className="font-semibold text-red-600">- {rupiah(discount)}</span></p>}
              <p><span className="text-slate-500">Perkiraan total: </span><span className="font-bold text-[#0B1F3A]" data-testid="inv-est-total">{rupiah(estTotal)}</span></p>
            </div>
            <button onClick={save} disabled={!form.customer_name || !form.items.some((i) => i.description)} data-testid="save-invoice" className="mt-4 w-full px-6 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-50">
              Simpan Invoice
            </button>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="delete-invoice-dialog">
          <div className="absolute inset-0 bg-black/40" onClick={() => !deleting && setConfirmDelete(null)} />
          <div className="relative w-full max-w-sm rounded-lg border border-slate-200 bg-white p-5 shadow-xl">
            <h2 className="font-display text-xl font-bold text-[#0B1F3A]">Hapus invoice?</h2>
            <p className="mt-2 text-sm text-slate-600">Invoice <strong>{confirmDelete.invoice_number}</strong> milik {confirmDelete.customer_name} akan dihapus permanen.</p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setConfirmDelete(null)} disabled={deleting} className="rounded-md border border-slate-300 px-4 py-2.5 text-sm font-bold disabled:opacity-50">Batal</button>
              <button type="button" onClick={remove} disabled={deleting} data-testid="confirm-delete-invoice" className="rounded-md bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50">{deleting ? "Menghapus..." : "Ya, Hapus"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
